import type { MenuItem, OptionChoice, OptionGroup } from './types';
import { choiceDeltaKurus, itemPriceKurus } from './money';

/**
 * Modifier seçimi (Faz 6 UI) — sunucu `validateModifierSelection` ile aynı kurallar; sunucu yeniden doğrular.
 * Seçim durumu: groupId → { choiceId → adet }. Nested grup (parentChoiceId) yalnız üst seçenek seçiliyken etkin.
 * Legacy API için `optionIds` (tekrar eden id = adet) üretilir.
 */
export type Selection = Record<string, Record<string, number>>;

export function maxOf(g: OptionGroup): number {
  if (g.maxSelection != null) return g.maxSelection;
  return g.selectionType === 'single' ? 1 : Number.POSITIVE_INFINITY;
}

export function quantityMaxOf(c: OptionChoice): number {
  return Math.max(1, c.quantityMax ?? 1);
}

function countOf(sel: Selection, g: OptionGroup): number {
  return Object.values(sel[g.id] ?? {}).reduce((a, b) => a + b, 0);
}

export function chosenIds(sel: Selection): Set<string> {
  const s = new Set<string>();
  for (const g of Object.values(sel)) for (const [id, q] of Object.entries(g)) if (q > 0) s.add(id);
  return s;
}

/** Etkin (görünür) gruplar: üst seçeneği olmayanlar + üst seçeneği seçili olanlar; sıralı. */
export function activeGroups(groups: OptionGroup[], sel: Selection): OptionGroup[] {
  const chosen = chosenIds(sel);
  return groups
    .filter((g) => !g.parentChoiceId || chosen.has(g.parentChoiceId))
    .sort((a, b) => a.tier - b.tier || (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

/** Varsayılanlar: her üst-seviyeli grupta isDefault seçenekler (max'a kadar). */
export function defaultSelection(groups: OptionGroup[]): Selection {
  const sel: Selection = {};
  for (const g of groups) {
    if (g.parentChoiceId) continue;
    const max = maxOf(g);
    const picked: Record<string, number> = {};
    let n = 0;
    for (const c of g.choices) {
      if (!c.isDefault || n >= max) continue;
      picked[c.id] = 1;
      n++;
    }
    sel[g.id] = picked;
  }
  return sel;
}

/** Nested grupların üst seçeneği kalktıysa alt seçimleri temizle. */
function prune(groups: OptionGroup[], sel: Selection): Selection {
  const chosen = chosenIds(sel);
  const out: Selection = { ...sel };
  let changed = false;
  for (const g of groups) {
    if (g.parentChoiceId && !chosen.has(g.parentChoiceId) && out[g.id] && Object.keys(out[g.id]).length) {
      out[g.id] = {};
      changed = true;
    }
  }
  return changed ? prune(groups, out) : out;
}

/** Tek seçim / çoklu seçim tıklaması. */
export function toggle(groups: OptionGroup[], sel: Selection, g: OptionGroup, choice: OptionChoice): Selection {
  const cur = sel[g.id] ?? {};
  const on = (cur[choice.id] ?? 0) > 0;
  let next: Record<string, number>;
  if (g.selectionType === 'single') {
    next = on ? (g.minSelection === 0 ? {} : cur) : { [choice.id]: 1 };
  } else if (on) {
    next = { ...cur };
    delete next[choice.id];
  } else if (countOf(sel, g) < maxOf(g)) {
    next = { ...cur, [choice.id]: 1 };
  } else {
    next = cur;
  }
  return prune(groups, { ...sel, [g.id]: next });
}

/** Adet artır/azalt (quantityMax > 1 olan seçenekler). Grup max'ı ve seçenek adedi sınırlar. */
export function setQuantity(groups: OptionGroup[], sel: Selection, g: OptionGroup, choice: OptionChoice, qty: number): Selection {
  const cur = { ...(sel[g.id] ?? {}) };
  const others = Object.entries(cur).filter(([id]) => id !== choice.id).reduce((a, [, q]) => a + q, 0);
  const q = Math.max(0, Math.min(qty, quantityMaxOf(choice), maxOf(g) - others));
  if (q === 0) delete cur[choice.id];
  else cur[choice.id] = q;
  return prune(groups, { ...sel, [g.id]: cur });
}

/** İlk karşılanmayan kural (etkin gruplar). */
export function unmet(groups: OptionGroup[], sel: Selection): string | null {
  for (const g of activeGroups(groups, sel)) {
    const n = countOf(sel, g);
    if (n < g.minSelection) return `${g.name}: en az ${g.minSelection} seçim`;
    if (n > maxOf(g)) return `${g.name}: en fazla ${maxOf(g)} seçim`;
  }
  return null;
}

export interface PricedSelection {
  unitKurus: number;
  modifiersKurus: number;
  optionIds: string[];
  names: string[];
}

/** Fiyat (kuruş) + legacy optionIds + görünen adlar. */
export function priceSelection(item: MenuItem, sel: Selection): PricedSelection {
  const base = itemPriceKurus(item);
  let modifiersKurus = 0;
  const optionIds: string[] = [];
  const names: string[] = [];
  for (const g of activeGroups(item.optionGroups, sel)) {
    for (const [id, q] of Object.entries(sel[g.id] ?? {})) {
      if (q <= 0) continue;
      const c = g.choices.find((x) => x.id === id);
      if (!c) continue;
      modifiersKurus += choiceDeltaKurus(c) * q;
      for (let i = 0; i < q; i++) optionIds.push(id);
      names.push(q > 1 ? `${q}× ${c.name}` : c.name);
    }
  }
  return { unitKurus: base + modifiersKurus, modifiersKurus, optionIds, names };
}
